import { useCallback, useEffect, useRef, useState } from 'react'

import { Box } from '@chakra-ui/react'
import { wwtMemo } from '@wwt/custom/wwt'

import { wwtPerformanceLog } from '@wwt/core/admin'
import { ReasonType } from '@wwt/entities/reason'
import { Offer } from '@wwt/entities/search-response'

import { wwtReasonLog } from '../logger/wwtReasonLog.ts'
import { DislikeContent } from './DislikeContent.tsx'
import { LikeContent } from './LikeContent.tsx'

export interface ReasonPopupProps {
	offer: Offer
	onDislikeCancel?: () => void
	onClose: () => void
	type: ReasonType
	isShowed: boolean
	onOpenReview: (offerId: string) => void
	onOpenInfo: () => void
	offerId: string
}

export const ReasonPopup = wwtMemo(
	({
		offer,
		onClose,
		type,
		isShowed,
		onDislikeCancel,
		onOpenReview,
		onOpenInfo,
		offerId
	}: ReasonPopupProps) => {
		const timeoutId = useRef<number | undefined>(undefined)
		const [isFixed, setIsFixed] = useState(false)

		const setPopupFixed = () => {
			if (timeoutId.current === undefined) return

			wwtReasonLog('Set fixed and clean timer', timeoutId.current)

			clearTimeout(timeoutId.current)
			timeoutId.current = undefined
			setIsFixed(true)
		}

		useEffect(() => {
			timeoutId.current = window.setTimeout(() => onClose(), 5000)
			wwtReasonLog('Create timer', timeoutId.current)

			return () => {
				wwtReasonLog('Clean timer', timeoutId.current)
				clearTimeout(timeoutId.current)
			}
		}, [isFixed])

		const handleOpenReview = useCallback(() => {
			onOpenReview(offerId)
		}, [onOpenReview, offerId])

		wwtPerformanceLog('ReasonPopup rerender')

		return (
			<Box
				data-wwt-id="reason-popup--popup"
				display={isShowed ? 'block' : 'none'}
				w={{ base: '100vw', md: type === 'like' ? '25rem' : '15.8125rem' }}
				minH={{ base: '3rem', md: '3.875rem' }}
				onMouseOver={setPopupFixed}
				transition="all 300ms"
			>
				{isShowed &&
					(type === ReasonType.like ? (
						<LikeContent
							isFixed={isFixed}
							onOpenInfo={onOpenInfo}
							onOpenReview={handleOpenReview}
							onClose={onClose}
							type={type}
							offerId={offerId}
						/>
					) : (
						type === ReasonType.dislike && (
							<DislikeContent
								isFixed={isFixed}
								offer={offer}
								onOpenInfo={onOpenInfo}
								onOpenReview={handleOpenReview}
								onDislikeCancel={onDislikeCancel}
								onClose={onClose}
								type={type}
								offerId={offerId}
							/>
						)
					))}
			</Box>
		)
	}
)

ReasonPopup.displayName = 'ReasonPopup'
